// src/pages/Farmer/FarmerVisitForm.jsx
import { useState, useEffect } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import farmerVisitService from '../../services/farmerVisit.service';
import Toast from '../../components/Toast/Toast';
import './FarmerVisitPages.css';

const CROP_OPTIONS = [
  'Wheat', 'Rice', 'Cotton', 'Sugarcane', 'Soybean',
  'Maize', 'Banana', 'Tomato', 'Onion', 'Potato',
];

const ACRE_OPTIONS = [
  '1 to 3 Acres',
  '4 to 6 Acres',
  '7 to 10 Acres',
  '10 to 20 Acres',
  'Above 20 Acres',
  'Above 100 Acres',
];

const formatDateTime = (dt) => {
  if (!dt) return '—';
  return new Date(dt).toLocaleDateString('en-IN', {
    day: '2-digit', month: 'short', year: 'numeric',
    hour: '2-digit', minute: '2-digit', hour12: true,
  });
};

const FarmerVisitForm = () => {
  const navigate              = useNavigate();
  const { village, farmerId } = useParams();
  const location              = useLocation();

  const isFollowup     = location.pathname.includes('followup');
  const visitType      = isFollowup ? 'FOLLOWUP' : 'FIRST_VISIT';
  const pageTitle      = isFollowup ? 'Followup Visit' : 'First Visit';
  const decodedVillage = decodeURIComponent(village);

  const farmerFromState = location.state?.farmer;

  const [farmer, setFarmer]       = useState(farmerFromState || null);
  const [lastVisit, setLastVisit] = useState(null);
  const [employees, setEmployees] = useState([]);
  const [toast, setToast]         = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errors, setErrors]       = useState({});
  const [submitted, setSubmitted] = useState(false);

  const [formData, setFormData] = useState({
    crop:           '',
    acre:           '',
    travelingWith:  '',
    visitLatitude:  '',
    visitLongitude: '',
    remark:         '',
    reason:         '', // ✅ followup reason
    
  });

  // Fetch last visit for followup
  useEffect(() => {
    if (isFollowup && farmerId) {
      farmerVisitService.getLastVisit(farmerId)
        .then(res => { if (res.success && res.data) setLastVisit(res.data); })
        .catch(() => {});
    }
  }, [farmerId, isFollowup]);

  // Fetch employees
  useEffect(() => {
    farmerVisitService.getTravelingWithEmployees()
      .then(res => { if (res.success && res.data) setEmployees(res.data); })
      .catch(() => {});
  }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    if (errors[name]) setErrors(prev => ({ ...prev, [name]: '' }));
  };

  const detectLocation = () => {
  if (!navigator.geolocation) {
    setToast({ message: 'Geolocation is not supported by your browser.', type: 'error' });
    return;
  }

  setToast({ message: 'Detecting location…', type: 'info' });

  let bestReading = null;
  let settled = false;

  const finish = (position) => {
  if (settled) return;
  settled = true;
  navigator.geolocation.clearWatch(watchId);

  const { latitude, longitude, accuracy } = position.coords;
  setFormData(prev => ({
    ...prev,
    visitLatitude: latitude.toFixed(8),      // was: latitude
    visitLongitude: longitude.toFixed(8),    // was: longitude
    locationAccuracy: accuracy,
  }));

  setToast({ message: '📍 Location detected!', type: 'success' });
};

  const watchId = navigator.geolocation.watchPosition(
    (position) => {
      if (!bestReading || position.coords.accuracy < bestReading.coords.accuracy) {
        bestReading = position;
      }
      // good enough — stop early
      if (position.coords.accuracy <= 15) {
        finish(position);
      }
    },
    (error) => {
      if (settled) return;
      settled = true;
      navigator.geolocation.clearWatch(watchId);
      const message =
        error.code === error.PERMISSION_DENIED
          ? 'Location access denied. Please enable it in your browser/app settings.'
          : error.code === error.TIMEOUT
          ? 'Location request timed out. Please try again.'
          : 'Unable to detect location. ';
      setToast({ message, type: 'error' });
      console.error('Geolocation error:', error);
    },
    { enableHighAccuracy: true, timeout: 15000, maximumAge: 0 }
  );

  // Hard stop after ~12s — use best reading we've got rather than hang forever
  setTimeout(() => {
    if (!settled && bestReading) {
      finish(bestReading);
    } else if (!settled) {
      settled = true;
      navigator.geolocation.clearWatch(watchId);
      setToast({ message: 'Could not get an accurate location. ', type: 'error' });
    }
  }, 12000);
};

  const validate = () => {
    const newErrors = {};

    // Location required for first visit
    if (!isFollowup) {
      if (!formData.visitLatitude)  newErrors.visitLatitude  = 'Latitude is required for first visit';
      if (!formData.visitLongitude) newErrors.visitLongitude = 'Longitude is required for first visit';
    }

    // Location required for followup (1000m check)
    if (isFollowup) {
      if (!formData.visitLatitude)  newErrors.visitLatitude  = 'Latitude is required to verify your location';
      if (!formData.visitLongitude) newErrors.visitLongitude = 'Longitude is required to verify your location';
      if (!formData.reason.trim())  newErrors.reason         = 'Reason for followup visit is required';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleBack = () => {
    if (isFollowup) {
      navigate('/farmer/farmer-visit');
    } else {
      navigate(`/farmer/first-visit/village/${village}`);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) return;

    setIsSubmitting(true);
    try {
      const payload = {
  farmerId:      parseInt(farmerId),
  visitType,
  crop:          formData.crop          || undefined,
  acre:          formData.acre          || undefined,
  travelingWith: formData.travelingWith ? parseInt(formData.travelingWith) : undefined,
  visitLatitude: formData.visitLatitude  || undefined,
  visitLongitude: formData.visitLongitude || undefined,
  locationAccuracy: formData.locationAccuracy || undefined, // NEW
  remark:        formData.remark        || undefined,
  reason:        formData.reason        || undefined,
};

      const res = await farmerVisitService.createVisit(payload);
      if (res.success) {
        setToast({
          message: isFollowup
            ? 'Followup visit logged successfully!'
            : 'First visit logged! Followup entry also created automatically.',
          type: 'success',
        });
        setSubmitted(true);
      }
    } catch (error) {
      const msg = error.response?.data?.message || 'Failed to log visit';
      setToast({ message: msg, type: 'error' });

      // If 1000m radius error — highlight location fields
      if (msg.includes('1000m') || msg.includes('radius') || msg.includes('distance')) {
        setErrors(prev => ({
          ...prev,
          visitLatitude:  'You are too far from the farmer\'s location',
          visitLongitude: 'You are too far from the farmer\'s location',
        }));
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="fv-container" style={{ maxWidth: 700 }}>
      {toast && (
        <Toast message={toast.message} type={toast.type} onClose={() => setToast(null)} />
      )}

      {/* Breadcrumb */}
      <nav className="fv-breadcrumb">
        <a href="/dashboard/overview">Dashboard</a>
        <span>›</span>
        <button
          className="fv-breadcrumb-btn"
          onClick={() => navigate(isFollowup ? '/farmer/farmer-visit' : '/farmer/first-visit')}
        >
          {pageTitle}
        </button>
        {!isFollowup && (
          <>
            <span>›</span>
            <button className="fv-breadcrumb-btn" onClick={handleBack}>
              {decodedVillage}
            </button>
          </>
        )}
        <span>›</span>
        <span>{farmer?.farmerName || 'Farmer'}</span>
      </nav>

      {/* Header */}
      <div className="fv-header">
        <div>
          <h4 className="fv-title">Farmer Visit</h4>
          <p className="fv-subtitle">{pageTitle} — {decodedVillage}</p>
        </div>
        <button className="fv-btn-back" onClick={handleBack}>← Back</button>
      </div>

      {/* Last visit info for followup */}
      {isFollowup && lastVisit && (
        <div className="fv-last-visit-card">
          <p className="fv-last-visit-title">📋 Last Visit Details</p>
          <div className="fv-last-visit-grid">
            <div className="fv-last-visit-row">
              <span className="fv-lv-label">Visited By</span>
              <span className="fv-lv-value">
                {lastVisit.user?.firstName} {lastVisit.user?.lastName}
              </span>
            </div>
            <div className="fv-last-visit-row">
              <span className="fv-lv-label">Visit Time</span>
              <span className="fv-lv-value">{formatDateTime(lastVisit.visitTime)}</span>
            </div>
            {lastVisit.crop && (
              <div className="fv-last-visit-row">
                <span className="fv-lv-label">Crop</span>
                <span className="fv-lv-value">{lastVisit.crop}</span>
              </div>
            )}
            {lastVisit.acre && (
              <div className="fv-last-visit-row">
                <span className="fv-lv-label">Acre</span>
                <span className="fv-lv-value">{lastVisit.acre}</span>
              </div>
            )}
            {lastVisit.reason && (
              <div className="fv-last-visit-row">
                <span className="fv-lv-label">Reason</span>
                <span className="fv-lv-value" style={{ color: '#c62828' }}>
                  {lastVisit.reason}
                </span>
              </div>
            )}
            {lastVisit.remark && (
              <div className="fv-last-visit-row">
                <span className="fv-lv-label">Remark</span>
                <span className="fv-lv-value">{lastVisit.remark}</span>
              </div>
            )}
          </div>
        </div>
      )}

      {submitted ? (
        <div className="fv-locked-card" style={{ background: '#f8fdf5', borderColor: '#aed581' }}>
          <div className="fv-locked-icon">✅</div>
          <h5 className="fv-locked-title" style={{ color: '#2e7d32' }}>
            Visit Logged Successfully!
          </h5>
          {!isFollowup && (
            <p className="fv-locked-text">
              First visit recorded. A followup entry has been automatically created.
            </p>
          )}
          <div style={{ display: 'flex', gap: 12, justifyContent: 'center', marginTop: 20 }}>
            <button className="fv-btn-secondary" onClick={handleBack}>
              ← Back
            </button>
            <button
              className="fv-btn-primary"
              onClick={() => {
                setSubmitted(false);
                setFormData({
                  crop: '', acre: '', travelingWith: '',
                  visitLatitude: '', visitLongitude: '',
                  remark: '', reason: '',
                });
              }}
            >
              Log Another Visit
            </button>
          </div>
        </div>
      ) : (
        <div className="fv-form-card">
          <p className="fv-form-title">Farmer Visit Details</p>
          <form onSubmit={handleSubmit}>

            {/* Farmer info — read only */}
            <div className="fv-form-row">
              <div className="fv-form-group">
                <label className="fv-label">Farmer Name</label>
                <input type="text" className="fv-input fv-input-readonly"
                  value={farmer?.farmerName || ''} readOnly />
              </div>
              <div className="fv-form-group">
                <label className="fv-label">Mobile No</label>
                <input type="text" className="fv-input fv-input-readonly"
                  value={farmer?.mobileNo || ''} readOnly />
              </div>
            </div>

            <div className="fv-form-row">
              <div className="fv-form-group">
                <label className="fv-label">Village</label>
                <input type="text" className="fv-input fv-input-readonly"
                  value={decodedVillage} readOnly />
              </div>
            </div>

            {/* Location — required for first visit, required for followup (1000m check) */}
            <div className="fv-form-row">
              <div className="fv-form-group">
                <label className="fv-label">
                  Latitude <span style={{ color: '#dc3545' }}>*</span>
                  {!isFollowup && (
                    <span style={{ fontSize: 11, color: '#6c757d', marginLeft: 4 }}>
                      (saved as farmer location)
                    </span>
                  )}
                  {isFollowup && (
                    <span style={{ fontSize: 11, color: '#6c757d', marginLeft: 4 }}>
                      (must be within 1000m of farmer)
                    </span>
                  )}
                </label>
                <div className="fv-location-group">
                  <input
                    type="text"
                    name="visitLatitude"
                    className={`fv-input ${errors.visitLatitude ? 'fv-input-error' : ''}`}
                    value={formData.visitLatitude}
                    onChange={handleChange}
                    placeholder="Enter latitude"
                  />
                  <button type="button" className="fv-btn-detect" onClick={detectLocation}>
                    📍 Detect
                  </button>
                </div>
                {errors.visitLatitude && (
                  <span style={{ fontSize: 12, color: '#dc3545' }}>{errors.visitLatitude}</span>
                )}
              </div>

              <div className="fv-form-group">
                <label className="fv-label">
                  Longitude <span style={{ color: '#dc3545' }}>*</span>
                </label>
                <input
                  type="text"
                  name="visitLongitude"
                  className={`fv-input ${errors.visitLongitude ? 'fv-input-error' : ''}`}
                  value={formData.visitLongitude}
                  onChange={handleChange}
                  placeholder="Enter longitude"
                />
                {errors.visitLongitude && (
                  <span style={{ fontSize: 12, color: '#dc3545' }}>{errors.visitLongitude}</span>
                )}
              </div>
            </div>

            {/* Crop + Acre */}
            <div className="fv-form-row">
              <div className="fv-form-group">
                <label className="fv-label">Crop</label>
                <select name="crop" className="fv-select" value={formData.crop} onChange={handleChange}>
                  <option value="">Select Crop</option>
                  {CROP_OPTIONS.map(c => <option key={c} value={c}>{c}</option>)}
                </select>
              </div>
              <div className="fv-form-group">
                <label className="fv-label">Acre</label>
                <select name="acre" className="fv-select" value={formData.acre} onChange={handleChange}>
                  <option value="">Select Acre Range</option>
                  {ACRE_OPTIONS.map(a => <option key={a} value={a}>{a}</option>)}
                </select>
              </div>
            </div>

            {/* Traveling With */}
            <div className="fv-form-row">
              <div className="fv-form-group">
                <label className="fv-label">Traveling With</label>
                <select name="travelingWith" className="fv-select" value={formData.travelingWith} onChange={handleChange}>
                  <option value="">Self</option>
                  {employees.map(emp => (
                    <option key={emp.id} value={emp.id}>
                      {emp.firstName} {emp.lastName}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            {/* Reason — only for followup, required */}
            {isFollowup && (
              <div className="fv-form-row">
                <div className="fv-form-group fv-full-width">
                  <label className="fv-label">
                    Reason for Followup <span style={{ color: '#dc3545' }}>*</span>
                  </label>
                  <textarea
                    name="reason"
                    className={`fv-input ${errors.reason ? 'fv-input-error' : ''}`}
                    value={formData.reason}
                    onChange={handleChange}
                    placeholder="Why are you visiting this farmer again? (e.g. Price discussion, crop issue, order follow up...)"
                    rows={3}
                    style={{ resize: 'vertical' }}
                  />
                  {errors.reason && (
                    <span style={{ fontSize: 12, color: '#dc3545' }}>{errors.reason}</span>
                  )}
                </div>
              </div>
            )}

            {/* Remark */}
            <div className="fv-form-row">
              <div className="fv-form-group fv-full-width">
                <label className="fv-label">Remark</label>
                <textarea
                  name="remark"
                  className="fv-input"
                  value={formData.remark}
                  onChange={handleChange}
                  placeholder="Additional notes (optional)"
                  rows={2}
                  style={{ resize: 'vertical' }}
                />
              </div>
            </div>

            {/* Actions */}
            <div className="fv-form-actions">
              <button type="button" className="fv-btn-secondary"
                onClick={handleBack} disabled={isSubmitting}>
                Cancel
              </button>
              <button type="submit" className="fv-btn-primary" disabled={isSubmitting}>
                {isSubmitting ? 'Submitting...' : 'Submit'}
              </button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
};

export default FarmerVisitForm;